import { FC, useContext, useEffect, useState } from 'react';
import { keyBy } from 'lodash-es';
import { CgChevronDown } from 'react-icons/cg';
import { MdOutlineArrowDownward } from 'react-icons/md';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  Portal,
  Separator,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  useApplicationFormContextLoader,
  useApplicationFormLoader,
  useApplicationFormSubmit,
  useApplicationTransition,
  useApplicationTransitionMetadata,
} from '~/core/shared/api/middle-office/application';
import { useRequiredParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import {
  ApplicationForm,
  DocumentStateResource,
  DocumentStatusResource,
  DocumentTransitionFormsResource,
  DocumentTransitionRequirementResource,
  DocumentTransitionRequirementStatus,
  FormContext,
  WorkflowStatusPair,
} from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { ApplicationUpdateForm } from './application/form';
import { Status } from './table/Status.component';
import { DocumentRequirementsChecklist } from './DocumentRequirementsChecklist.component';
import { LayoutContext } from './Layout.component';

type DocumentStatusSelectProps = {
  documentUuid: string;
  documentDefinition: string;
  state: DocumentStateResource | undefined;
  status?: DocumentStatusResource | undefined;
  workflowStatusPair: WorkflowStatusPair;
  onStateUpdate: (state: DocumentStateResource) => void;
  makeDirectStatusTransitions?: boolean;
};

type ApplicationFormWithContext = {
  form: ApplicationForm;
  context: Record<string, FormContext>;
};

const isObjEmpty = (obj: any) => Object.keys(obj).length === 0;

export const DocumentStatusSelect: FC<DocumentStatusSelectProps> = ({
  state,
  status,
  documentUuid,
  documentDefinition,
  onStateUpdate,
  workflowStatusPair,
  makeDirectStatusTransitions = false,
}) => {
  const { workflowDefinition, statusDefinition } = workflowStatusPair;
  const { uuid } = useRequiredParams(['uuid']);
  const { fetchTransitionMetadata } = useApplicationTransitionMetadata(
    documentDefinition,
    workflowDefinition,
  );
  const { performTransition } = useApplicationTransition(
    documentDefinition,
    workflowDefinition,
  );
  const { submitForm, isPending } =
    useApplicationFormSubmit(documentDefinition);
  const { loadForm } = useApplicationFormLoader(documentDefinition);
  const { loadFormContext } =
    useApplicationFormContextLoader(documentDefinition);
  const { openSidebarModalComponent, closeSidebarModalComponent } =
    useContext(LayoutContext);

  const [requirements, setRequirements] = useState<
    DocumentTransitionRequirementResource[]
  >([]);
  const [forms, setForms] = useState<DocumentTransitionFormsResource[]>([]);
  const [currentStatus, setCurrentStatus] = useState<DocumentStatusResource>();
  const [nextStatus, setNextStatus] = useState<DocumentStatusResource>();
  const [transition, setTransition] = useState<string>();

  const [loadedForms, setLoadedForms] = useState<ApplicationFormWithContext[]>(
    [],
  );
  const [isLoadingForms, setIsLoadingForms] = useState<boolean>(false);

  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);

  const [validationErrors, setValidationErrors] = useState<Record<string, any>>(
    { initial: true },
  );

  const makeTransition = useEventHandler(
    async (
      currentWorkflow: string,
      newTransition: string,
    ): Promise<boolean> => {
      try {
        const stateResponse = await performTransition({
          uuid: documentUuid,
          data: { transition: newTransition, workflow: currentWorkflow },
        });
        if (stateResponse?.data) {
          onStateUpdate(stateResponse.data);
        }
        return true;
      } catch {
        // Error handling is already done in the useApplicationTransition hook
        return false;
      }
    },
  );

  const prepareMakeDirectTransition = useEventHandler(
    async (currentWorkflow: string, newTransition: string): Promise<void> => {
      await makeTransition(currentWorkflow, newTransition);
    },
  );

  const prepareMakeTransition = useEventHandler(
    async (currentWorkflow: string, newTransition: string): Promise<void> => {
      const response = await fetchTransitionMetadata({
        uuid: documentUuid,
        data: {
          statusDefinition,
          transition: newTransition,
          workflow: currentWorkflow,
        },
      });

      if (response?.data) {
        const transitionMetadata = response.data;
        setRequirements(transitionMetadata.requirements);
        setForms(transitionMetadata.forms);
        setCurrentStatus(transitionMetadata.currentStatus);
        setNextStatus(transitionMetadata.nextStatus);
        setTransition(newTransition);
      }
    },
  );

  const updateRequirementStatus = useEventHandler(
    async (
      requirement: string,
      newStatus: DocumentTransitionRequirementStatus,
    ): Promise<void> => {
      setRequirements((prevRequirements) =>
        prevRequirements.map((r) => {
          if (r.key === requirement) {
            return {
              ...r,
              status: newStatus,
            };
          }
          return r;
        }),
      );
    },
  );

  useEffect(() => {
    if (isObjEmpty(validationErrors) && transition && loadedForms.length > 0) {
      makeTransition(workflowDefinition, transition);
      closeSidebarModalComponent();
    } else {
      setIsLoadingStatus(false);
    }
  }, [validationErrors]);

  useEffect(() => {
    setIsLoadingStatus(false);
  }, [status]);

  const loadFormData = useEventHandler(
    async (applicationUuid: string, formKey: string): Promise<void> => {
      const formResponse = await loadForm({ uuid: applicationUuid, formKey });
      const contextResponse = await loadFormContext({
        uuid: applicationUuid,
        formKey,
      });
      const form = formResponse?.data;
      const context = contextResponse?.data;
      if (form && context) {
        setLoadedForms((current) => [
          ...current,
          {
            form,
            context: keyBy(context, (c) => c.name),
          },
        ]);
      }
    },
  );

  const loadAllForms = useEventHandler(async () => {
    setIsLoadingForms(true);
    setLoadedForms([]);

    if (forms)
      await Promise.all(
        forms.map((unloadedForm) =>
          loadFormData(uuid, unloadedForm.formKey.key),
        ),
      );

    setIsLoadingForms(false);
  });

  useEffect(() => {
    loadAllForms();
  }, [forms]);

  const onSubmit = useEventHandler(async (formValues: any): Promise<void> => {
    const { formKey, ...restOfFormValues } = formValues;

    try {
      await submitForm({
        uuid: uuid ?? '',
        data: restOfFormValues,
        params: { form: formKey },
      });
      setValidationErrors({});
    } catch (error: any) {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          setValidationErrors(errors);
          const errStr = Object.values(errors as Record<string, string[]>)
            .map((v) => v[0])
            .join(' ');
          handleError(errStr);
        }
      } else {
        handleError(
          error?.response?.data?.error?.message ??
            error?.message ??
            'Update failed',
        );
      }
    }
  });

  useEffect(() => {
    if (!transition) return;

    const requirementsCovered =
      !requirements ||
      !requirements.length ||
      !requirements.filter(
        (r) => !['pass', 'not_applicable'].includes(r.status),
      ).length;

    openSidebarModalComponent(
      'Change status',
      <Box maxWidth="500px" minWidth="300px">
        <Flex direction="column">
          <Flex whiteSpace="nowrap">
            <Text fontWeight="bold" mr="2" w="50px">
              From:
            </Text>
            <Status status={currentStatus} />
          </Flex>
          <Icon
            as={MdOutlineArrowDownward}
            boxSize="24px"
            color="blue.500"
            mb="2"
            mt="1"
          />
          <Flex whiteSpace="nowrap">
            <Text fontWeight="bold" mr="2" w="50px">
              To:
            </Text>
            <Status status={nextStatus} />
          </Flex>
          <Separator my="6" />
          {requirements && requirements.length > 0 && (
            <DocumentRequirementsChecklist
              documentDefinition={documentDefinition}
              documentUuid={documentUuid}
              onRequirementUpdate={updateRequirementStatus}
              requirements={requirements}
              transition={transition}
              workflow={workflowDefinition}
            />
          )}

          {isLoadingForms && (
            <Flex justifyContent="center" py={4}>
              <Spinner
                animationDuration="0.5s"
                borderWidth="2px"
                color="primary.500"
                size="xl"
              />
            </Flex>
          )}

          {loadedForms.map((loadedForm, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} mt={4}>
              <ApplicationUpdateForm
                form={loadedForm.form}
                formContexts={loadedForm.context}
                hideLeftPanel
                isSaving={isPending}
                onSubmit={onSubmit}
                showHiddenSave
                validationErrors={validationErrors}
              />
            </Box>
          ))}
        </Flex>
        <Stack direction="row" justifyContent="end" mt={6}>
          <Button
            colorPalette="gray"
            fontSize="md"
            fontWeight="normal"
            onClick={closeSidebarModalComponent}
            variant="outline"
          >
            Cancel
          </Button>

          <Button
            colorPalette="primary"
            disabled={!requirementsCovered || isLoadingForms}
            fontSize="md"
            fontWeight="bold"
            onClick={async () => {
              setIsLoadingStatus(true);
              if (loadedForms && loadedForms.length > 0) {
                if (document !== null) {
                  loadedForms.map(async (loadedForm) => {
                    const el = document.getElementById(loadedForm.form.key);

                    if (el) {
                      el.click();
                    }
                  });
                }
              } else {
                const success = await makeTransition(
                  workflowDefinition,
                  transition,
                );
                if (success) {
                  closeSidebarModalComponent();
                }
              }
            }}
            type="submit"
            variant="solid"
          >
            Change status
          </Button>
        </Stack>
      </Box>,
    );
  }, [
    requirements,
    isLoadingForms,
    currentStatus,
    nextStatus,
    transition,
    loadedForms,
  ]);

  return (
    <Box position="relative">
      <Menu.Root>
        <Menu.Trigger asChild>
          <Button
            borderColor="gray.200"
            borderRadius="md"
            color="black"
            colorPalette="gray"
            fontWeight="normal"
            loading={isLoadingStatus}
            size="md"
            variant="outline"
          >
            {status?.title || state?.place?.meta?.title || 'Open'}
            <CgChevronDown />
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content position="relative" top="">
              {state?.transitions?.length ? (
                state.transitions.map(({ meta, name }) => (
                  <Menu.Item
                    key={`transition-${name}`}
                    onClick={() =>
                      makeDirectStatusTransitions
                        ? prepareMakeDirectTransition(workflowDefinition, name)
                        : prepareMakeTransition(workflowDefinition, name)
                    }
                    value={meta?.title || name}
                  >
                    {meta?.title || name}
                  </Menu.Item>
                ))
              ) : (
                <Menu.Item value="...">...</Menu.Item>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );
};
